import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface UnscheduledCampaign {
  id: string;
  title: string;
  description: string;
  type: string;
  validFrom: string;
  validTo: string;
  flyer: any;
  event: any;
  offerDetails: string;
  tagLine: string;
}

interface UnscheduledCampaignsModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaigns: UnscheduledCampaign[];
  loading: boolean;
  onCampaignClick: (campaignId: string, campaignType: string) => void;
}

const UnscheduledCampaignsModal: React.FC<UnscheduledCampaignsModalProps> = ({
  isOpen,
  onClose,
  campaigns,
  loading,
  onCampaignClick
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="rounded-2xl p-4 max-w-7xl w-full max-h-[85vh] overflow-hidden flex flex-col"
            style={{
              backdropFilter: 'blur(10px)',
              boxShadow: '0 0 14px 0 #0000001a',
              background: 'linear-gradient(90deg, #212121eb, #373737eb)',
              border: '1px solid #e0e0e021'
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-medium text-[#EE4423]">
                All Unscheduled Campaigns
              </h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-800 rounded-full transition-colors"
              >
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Campaigns Grid */}
            <div className="overflow-y-auto flex-1 pr-2 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="text-gray-400">Loading campaigns...</div>
                </div>
              ) : campaigns.length === 0 ? (
                <div className="flex items-center justify-center py-20">
                  <div className="text-gray-400">No unscheduled campaigns found</div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  {campaigns.map((campaign) => {
                    const flyerImage = campaign.flyer?.image?.storage_url || 
                                      campaign.flyer?.mediaType?.[0]?.image?.storage_url || 
                                      'https://via.placeholder.com/280x192?text=No+Image';
                    
                    const validFromDate = new Date(campaign.validFrom);
                    const formattedDate = validFromDate.toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric', 
                      year: 'numeric' 
                    });

                    return (
                      <motion.div
                        key={campaign.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        onClick={() => {
                          onClose();
                          onCampaignClick(campaign.id, campaign.type);
                        }}
                        className="rounded-2xl overflow-hidden border border-gray-700/50 cursor-pointer hover:border-[#EE4423] transition-all"
                        style={{
                          backdropFilter: 'blur(10px)',
                          boxShadow: '0 0 14px 0 #0000001a'
                        }}
                      >
                        <div className="relative h-48 overflow-hidden">
                          <img
                            src={flyerImage}
                            alt={campaign.title}
                            className="w-full h-full object-cover object-top"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = 'https://via.placeholder.com/280x192?text=No+Image';
                            }}
                          />
                        </div>

                        <div 
                          className="p-4"
                          style={{
                            background: "rgb(55,55,55)",
                          }}
                        >
                          <h3 className="text-white font-medium text-xs mb-2 line-clamp-2">{campaign.title}</h3>
                          <p className="text-gray-400 text-[10px]">{formattedDate}</p>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default UnscheduledCampaignsModal;
